/**
 * DOM helpers for the Document Picture-in-Picture call window.
 *
 * The call UI is not re-rendered inside the PiP window. The existing elements
 * (tile container, stream controls) are moved into the PiP document and put back
 * when it closes. This keeps a single <video> element per stream, which
 * ConferenceService.bindElement and the local media pipe sink depend on, and keeps
 * the Angular views (and their injectors) intact.
 */

const STYLE_SELECTOR = 'style, link[rel="stylesheet"]';

/** a moved element and the comment node that marks its original position */
interface IMovedElement {
  element: HTMLElement;
  placeholder: Comment;
}

export class PipDocumentSession {
  private moved: IMovedElement[] = [];
  private headObserver: MutationObserver;
  private rootObserver: MutationObserver;
  private root: HTMLElement;
  // main document style node -> its copy in the pip document
  private clones = new Map<Node, Node>();

  constructor(
    private source: Document,
    private target: Document
  ) {}

  /** copy styles and root attributes, and keep them in sync until destroy() */
  public init(rootClass: string) {
    this.copyStyles();
    this.syncRoot();

    this.headObserver = new MutationObserver((mutations) =>
      this.onHeadMutations(mutations)
    );
    // Angular adds component styles when a component is first created. ThemeService rewrites
    // the rules of its <style> in place (custom css from settings/appearance).
    this.headObserver.observe(this.source.head, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // ThemeService sets data-av-* flags and css variables on <html>
    this.rootObserver = new MutationObserver(() => this.syncRoot());
    this.rootObserver.observe(this.source.documentElement, { attributes: true });
    this.rootObserver.observe(this.source.body, {
      attributes: true,
      attributeFilter: ["class"]
    });

    this.root = this.target.createElement("div");
    this.root.className = rootClass;
    this.target.body.appendChild(this.root);
  }

  /**
   * copy the <html> attributes and inline style. ThemeService writes the --av-* variables
   * and data-av-* flags there.
   */
  private syncRoot() {
    const from = this.source.documentElement;
    const to = this.target.documentElement;
    for (const attr of Array.from(to.attributes)) {
      if (!from.hasAttribute(attr.name)) {
        to.removeAttribute(attr.name);
      }
    }
    for (const attr of Array.from(from.attributes)) {
      to.setAttribute(attr.name, attr.value);
    }
    this.target.body.className = this.source.body.className;
  }

  /** move an element into the pip document, leaving a placeholder behind */
  public adopt(element: HTMLElement) {
    if (!element || !this.root || this.root.contains(element)) {
      return;
    }
    // re-created by the view (e.g. an @if toggled while in pip). drop the stale entry
    this.moved = this.moved.filter((m) => {
      if (!m.element.isConnected) {
        m.placeholder.remove();
        return false;
      }
      return true;
    });
    const placeholder = this.source.createComment("pip-placeholder");
    element.before(placeholder);
    this.root.appendChild(element);
    this.moved.push({ element, placeholder });
  }

  /** put every moved element back where it came from and stop syncing */
  public destroy() {
    this.headObserver?.disconnect();
    this.rootObserver?.disconnect();
    this.clones.clear();
    for (const { element, placeholder } of this.moved) {
      // the view may have removed the element while it was in pip
      if (element.isConnected && placeholder.isConnected) {
        placeholder.before(element);
      }
      placeholder.remove();
    }
    this.moved = [];
    this.root?.remove();
    this.root = null;
  }

  private copyStyles() {
    if (!this.target.head.querySelector("base")) {
      const base = this.target.createElement("base");
      // the pip document is about:blank. resolve relative asset urls against the app
      base.href = this.source.baseURI;
      this.target.head.appendChild(base);
    }

    this.source.head
      .querySelectorAll(STYLE_SELECTOR)
      .forEach((node) => this.cloneStyle(node));
  }

  private cloneStyle(node: Node) {
    // cloning keeps the CSP nonce
    const clone = node.cloneNode(true);
    this.target.head.appendChild(clone);
    this.clones.set(node, clone);
  }

  private onHeadMutations(mutations: MutationRecord[]) {
    for (const m of mutations) {
      if (m.target === this.source.head) {
        m.addedNodes.forEach((node) => {
          if (node instanceof Element && node.matches(STYLE_SELECTOR)) {
            this.cloneStyle(node);
          }
        });
        m.removedNodes.forEach((node) => {
          (this.clones.get(node) as ChildNode)?.remove();
          this.clones.delete(node);
        });
        continue;
      }

      // the text of an existing <style> changed
      const parent =
        m.target instanceof Element ? m.target : m.target.parentElement;
      const style = parent?.closest("style");
      const clone = style && this.clones.get(style);
      if (clone) {
        clone.textContent = style.textContent;
      }
    }
  }
}

/** Document Picture-in-Picture API. Not in the TypeScript DOM lib yet. */
export interface IDocumentPictureInPicture extends EventTarget {
  readonly window: Window | null;
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
}

export function getDocumentPictureInPicture():
  | IDocumentPictureInPicture
  | undefined {
  return (window as unknown as { documentPictureInPicture?: IDocumentPictureInPicture })
    .documentPictureInPicture;
}
